import { useTranslation } from 'react-i18next';
import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { CloudUpload } from 'lucide-react';
import { fmtDate } from './helpers';

const API = process.env.REACT_APP_BACKEND_URL;

export const UploadTab = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [outgoingRecords, setOutgoingRecords] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [selectedTokenId, setSelectedTokenId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null); // { ok: bool, message: string, ref?: string }
  const [uploadMonthFilter, setUploadMonthFilter] = useState('');
  const [uploadStatusFilter, setUploadStatusFilter] = useState('issued');

  const fetchInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status: uploadStatusFilter || 'issued', limit: 200 });
      if (uploadMonthFilter) params.append('month', uploadMonthFilter);
      const r = await axios.get(`${API}/api/invoices?${params}`);
      setInvoices(r.data || []);
    } catch {}
  }, [uploadStatusFilter, uploadMonthFilter]);

  const fetchOutgoing = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/ksef/invoices?direction=outgoing&limit=200`);
      setOutgoingRecords(r.data.records || []);
    } catch {}
  }, []);

  const fetchTokens = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/ksef/tokens`);
      // Endpoint returns { tokens: [...], total: N } (some older clients
      // expect a flat array — accept either shape).
      const raw = Array.isArray(r.data) ? r.data : (r.data?.tokens || []);
      setTokens(raw.filter(t => t.active && (t.permissions || []).includes('upload')));
    } catch {}
  }, []);

  useEffect(() => { fetchInvoices(); fetchOutgoing(); fetchTokens(); }, [fetchInvoices, fetchOutgoing, fetchTokens]);

  const handleUpload = async () => {
    if (!selectedInvoiceId) { toast.error(t('auto.misc2.pleaseSelectAnInvoice')); return; }
    setUploading(true);
    setUploadResult(null);
    try {
      const url = selectedTokenId
        ? `${API}/api/ksef/upload/${selectedInvoiceId}?token_id=${selectedTokenId}`
        : `${API}/api/ksef/upload/${selectedInvoiceId}`;
      const r = await axios.post(url);
      if (r.data.success) {
        const ref = r.data.ksef_reference_number || r.data.invoice_reference_number || '';
        toast.success(`Uploaded. Ref: ${ref}`);
        setUploadResult({ ok: true, message: `Invoice accepted by KSeF.`, ref });
      } else {
        // Keep dialog open so the user can read the full error inline.
        const msg = r.data.error || 'Upload failed';
        toast.error(msg, { duration: 6000 });
        setUploadResult({ ok: false, message: msg });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Upload failed';
      toast.error(msg, { duration: 6000 });
      setUploadResult({ ok: false, message: msg });
    } finally {
      setUploading(false);
      // Refresh in both success and failure paths
      fetchOutgoing();
      fetchInvoices();
    }
  };

  const handleDialogChange = (open) => {
    // Block dialog close while a request is in flight to avoid losing
    // visibility on the long-running KSeF round-trip.
    if (uploading) return;
    setUploadDialogOpen(open);
    if (!open) {
      setUploadResult(null);
      setSelectedInvoiceId('');
    }
  };

  return (
    <>
      <Card>
        <CardHeader><CardTitle className="text-sm">{t('auto.misc2.uploadInvoicesToKsef')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <Label className="text-xs">{t('auto.misc2.monthFilter')}</Label>
              <Input type="month" value={uploadMonthFilter} onChange={e => setUploadMonthFilter(e.target.value)} className="h-8 w-40" />
            </div>
            <div>
              <Label className="text-xs">{t('auto.misc2.status')}</Label>
              <Select value={uploadStatusFilter} onValueChange={setUploadStatusFilter}>
                <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="issued">{t('auto.misc2.issued')}</SelectItem>
                  <SelectItem value="draft">{t('auto.misc2.draft')}</SelectItem>
                  <SelectItem value="all">{t('auto.misc2.all')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={() => { setUploadResult(null); setUploadDialogOpen(true); }} data-testid="upload-invoice-btn">
              <CloudUpload className="h-3.5 w-3.5 mr-1" />{t('auto.misc2.uploadInvoice')}
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('auto.misc2.invoiceNumber')}</TableHead>
                <TableHead>{t('auto.misc2.ksefReference')}</TableHead>
                <TableHead>{t('auto.misc2.status')}</TableHead>
                <TableHead>{t('auto.misc2.submitted')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {outgoingRecords.length === 0
                ? <TableRow><TableCell colSpan={4} className="text-center text-gray-400">{t('auto.misc2.noInvoicesUploadedYet')}</TableCell></TableRow>
                : outgoingRecords.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>{r.invoice_number || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{r.ksef_reference_number?.slice(0, 30)}…</TableCell>
                      <TableCell><Badge variant={r.status === 'accepted' ? 'default' : 'secondary'}>{r.status}</Badge></TableCell>
                      <TableCell>{fmtDate(r.submitted_at)}</TableCell>
                    </TableRow>
                  ))
              }
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={uploadDialogOpen} onOpenChange={handleDialogChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>{t('auto.misc2.uploadInvoiceToKsef')}</DialogTitle>
            <DialogDescription>{t('auto.misc2.selectAnIssuedInvoiceToSubmit')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 min-w-0">
            <Label>{t('auto.misc2.selectInvoice')}</Label>
            <Select value={selectedInvoiceId} onValueChange={setSelectedInvoiceId} disabled={uploading || !!uploadResult?.ok}>
              <SelectTrigger className="w-full max-w-full overflow-hidden" data-testid="upload-invoice-select">
                <SelectValue placeholder={t('auto.misc2.chooseInvoice')} />
              </SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-2rem)]">
                {invoices.map(inv => {
                  const dateStr = inv.issue_date ? new Date(inv.issue_date).toISOString().slice(0, 10) : '';
                  const cust = inv.customer_name || '—';
                  return (
                    <SelectItem key={inv.id} value={inv.id}>
                      <span className="truncate inline-block max-w-[34rem] align-middle">
                        {inv.invoice_number} · {dateStr} · {cust} · {inv.total_gross} {inv.currency || 'PLN'}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 min-w-0">
            <Label>
              {t('auto.misc2.ksefTokenEnvironment')}
              <span className="ml-1 text-xs font-normal text-gray-500">{t('auto.misc2.leaveAsDefaultToUseThe')}</span>
            </Label>
            <Select
              value={selectedTokenId || 'default'}
              onValueChange={v => setSelectedTokenId(v === 'default' ? '' : v)}
              disabled={uploading || !!uploadResult?.ok}
            >
              <SelectTrigger className="w-full max-w-full overflow-hidden" data-testid="upload-token-select">
                <SelectValue placeholder={t('auto.misc2.defaultEnv')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t('auto.misc2.defaultUseEnvSetting')}</SelectItem>
                {tokens.map(t => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name} · {(t.environment || '').toUpperCase()} · NIP {t.nip}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tokens.length === 0 && (
              <p className="text-xs text-amber-600">
                {t('auto.misc2.noKsefTokensWithUploadPermission')}
              </p>
            )}
          </div>
          {uploading && (
            <div
              className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-700 flex items-center gap-2"
              data-testid="upload-progress"
            >
              <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
              Uploading to KSeF — this can take up to 30 s on the demo environment…
            </div>
          )}
          {uploadResult && uploadResult.ok && (
            <div
              className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800"
              data-testid="upload-success"
            >
              <div className="font-semibold">{t('auto.misc2.submittedToKsef')}</div>
              {uploadResult.ref && (
                <div className="mt-1 break-all">
                  Reference: <span className="font-mono text-xs">{uploadResult.ref}</span>
                </div>
              )}
              <div className="mt-1 text-xs text-emerald-700">
                The KSeF number may take a few seconds to appear. Refresh the records table if you don&apos;t see it yet.
              </div>
            </div>
          )}
          {uploadResult && !uploadResult.ok && (
            <div
              className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 max-w-full overflow-hidden"
              data-testid="upload-error"
            >
              <div className="font-semibold">{t('auto.misc2.ksefRejectedTheUpload')}</div>
              {/missing-permissions|InvoiceWrite|403/i.test(uploadResult.message || '') && (
                <div className="mt-2 rounded bg-amber-50 border border-amber-200 px-2 py-1.5 text-xs text-amber-900 space-y-1.5">
                  <div>
                    <strong>{t('auto.misc2.whatThis403ActuallyMeans')}</strong> KSeF says the
                    NIP&apos;s context doesn&apos;t have <code className="mx-0.5 px-1 bg-white rounded border border-amber-300">{t('auto.misc2.invoicewrite')}</code>
                    granted at the <em>entity</em> level — independent of what
                    you ticked when generating the token (token permissions are
                    capped by entity permissions).
                  </div>
                  <div>
                    <strong>{t('auto.misc2.howToVerify')}</strong> Open the
                    <em> {t('auto.misc2.tokens')} </em> {t('auto.misc2.tabClick')} <strong>{t('auto.misc2.diagnose')}</strong> next
                    to your token. It hits
                    <code className="mx-0.5 px-1 bg-white rounded border border-amber-300">/v2/permissions/query/personal/grants</code>
                    and lists every permission KSeF has registered for this
                    context. If <code className="mx-0.5 px-1 bg-white rounded border border-amber-300">{t('auto.misc2.invoicewrite')}</code>
                    is missing, the entity&apos;s admin needs to grant it via the
                    KSeF Demo Taxpayer App
                    (<a href="https://ksef-demo.mf.gov.pl" target="_blank" rel="noreferrer" className="underline">ksef-demo.mf.gov.pl</a>)
                    before the upload can succeed.
                  </div>
                </div>
              )}
              <div className="mt-2 max-h-40 overflow-auto break-all whitespace-pre-wrap font-mono text-xs leading-relaxed">
                {uploadResult.message}
              </div>
              <div className="mt-2 text-xs text-red-700">
                {t('auto.misc2.theXmlPayloadWasSavedWith')}
              </div>
            </div>
          )}
          <DialogFooter>
            {uploadResult?.ok ? (
              <Button onClick={() => handleDialogChange(false)} data-testid="upload-done-btn">{t('auto.misc2.done')}</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => handleDialogChange(false)} disabled={uploading} data-testid="dialog-uploadtab-outline-btn">
                  {uploadResult ? t('auto.misc2.close') : t('auto.misc2.cancel')}
                </Button>
                <Button onClick={handleUpload} disabled={uploading || !selectedInvoiceId} data-testid="confirm-upload-btn">
                  {uploading ? 'Uploading…' : (uploadResult ? t('auto.misc2.retryUpload') : t('auto.misc2.uploadToKsef'))}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
